import {
  Box,
  Button,
  Col,
  Dropdown,
  ListGroup,
  ListItem,
  Modal,
  Overlay,
} from '@cubeyou-team/hx-react-components';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { getProductScope } from '@discover-frontend/utils';
import { updateDataSource } from 'src/api/me/request';
import { supportsSegmentationAndCompare } from 'src/utils/features';
import {
  TrialRestricted,
  TRIAL_FEATURES,
  useTrialPaywall,
  useGuardAnalyze,
} from 'src/modules/trial-paywall';
import {
  addAudienceToCompare,
  openCompareAfterSave,
  setError,
  setLoadingStatus,
} from '../../../../actions';
import { API, APPS_MAP } from '../../../../constants/config';
import translations from '../../../../lang';
import { checkQuery } from '../../../../operations/query';
import { prepareNewAudience } from '../../../../operations/repository';
import { getTargets } from '../../../../selectors/queryBuilder';
import ApiRequestHeader from '../../../../utils/ApiRequestHeader';
import { fetchWrapper } from '../../../../utils';
import { isAvailableAddon } from '../../../../utils/AuthHelperMethods';
import { isEmpty } from '../../../../utils/misc';

const StyledActionItem = styled.div`
  & i {
    color: ${(props) => props.theme.colors[props.color]};
  }
`;

const saveAudience = async (target, dataSource) => {
  const base = API.WORKSPACE.BASE_URL;
  const url = `${base}/repository/audiences`;
  const authRequestHeader = ApiRequestHeader();
  const audience = {
    ...target,
    label: target.target_label,
    data_source: dataSource,
  };
  const newAudience = prepareNewAudience(audience);
  newAudience.scope = getProductScope();

  try {
    const { response, error } = await fetchWrapper(
      url,
      authRequestHeader,
      'POST',
      newAudience
    );

    if (!error) {
      return { response: response.data };
    }

    return { error };
  } catch (err) {
    return err;
  }
};

const QueryBuilderActions = ({
  runButtonLabel,
  isQueryValid,
  isQueryLoading,
  onRequestCreateAudience,
}) => {
  const [runButtonIsOpen, setRunButtonIsOpen] = useState(false);
  const targets = useSelector(getTargets);
  const isCompareQuery = useSelector(
    (state) => state.queryBuilder.isCompareQuery
  );
  const tabs = useSelector((state) => state.tabs);
  const dataSource = useSelector((state) => state.queryBuilder.dataSource);
  const dispatch = useDispatch();
  const { guardFeature } = useTrialPaywall();
  const { guardAnalyze, isCheckingTrialStatus } = useGuardAnalyze();
  const hasSameTargets = useMemo(() => {
    const mapElement = (element) => {
      const args = element.arguments_list.slice().map((arg) => {
        if (arg.type === 'block') {
          return `[${mapElement(arg)}]`;
        } else {
          return arg.id;
        }
      });
      const orderedArgs = args.sort();
      const mappedArgs = orderedArgs.reduce((acc, arg) => {
        return `${acc}${acc ? element.operator : ''}${arg}`;
      }, '');
      return mappedArgs;
    };

    const queryTypes = targets.reduce((acc, target) => {
      if (!acc[target.query_type]) {
        acc[target.query_type] = [];
      }
      acc[target.query_type].push(target.target_id);
      return acc;
    }, {});

    const hasSameTargetsQuery = (list) => {
      const set = new Set(
        list.map((target) => {
          return mapElement(target.target_query);
        })
      );
      return set.size !== list.length;
    };

    const hasSameBaseQuery = (list) => {
      const set = new Set(
        list.map((target) => {
          let base = '';
          if (target.base_query && target.base_query.query_block) {
            const queryBlock = target.base_query.query_block;
            const args = queryBlock.arguments_list.slice().map((arg) => {
              return arg.id;
            });
            const orderedArgs = args.sort();
            base = orderedArgs.reduce((acc, arg) => {
              return `${acc}${arg}`;
            }, '');
          }
          return base;
        })
      );

      return set.size !== list.length;
    };
    const hasSameAudienceTimeframe = (list) => {
      const set = new Set(
        targets.map((target) => {
          return target.audience_timeframe;
        })
      );
      return set.size !== list.length;
    };
    const hasSameResultsTimeframe = (list) => {
      const set = new Set(
        list.map((target) => {
          return target.results_timeframe;
        })
      );
      return set.size !== list.length;
    };

    let result = false;

    Object.keys(queryTypes).forEach((type) => {
      if (type && queryTypes[type]) {
        const list = targets.filter((target) =>
          queryTypes[type].includes(target.target_id)
        );

        result =
          result ||
          (type === 'interest' && hasSameTargetsQuery(list)) ||
          (type === 'engagement' &&
            hasSameTargetsQuery(list) &&
            hasSameBaseQuery(list) &&
            hasSameAudienceTimeframe(list) &&
            hasSameResultsTimeframe(list));
      }
    });
    return result;
  }, [targets]);
  const [showHasSameTargetsModal, setShowHasSameTargetsModal] = useState(false);

  const handleCompareAudiences = useCallback(async () => {
    dispatch(setLoadingStatus(true));
    const audiences = [];
    const errors = [];
    await Promise.all(
      targets.map(async (target, index) => {
        const { response, error } = await saveAudience(target, dataSource);

        if (error) {
          errors.push({
            ...error,
            target: target.target_label || `Target audience ${index + 1}`,
          });
        } else {
          audiences.push({ ...response, color: target.target_color });
        }
      })
    );
    if (isEmpty(errors)) {
      dispatch(openCompareAfterSave(audiences));
    } else {
      const errorMessage = errors.reduce((acc, e) => {
        acc = `${acc}${acc.length > 0 ? ', ' : ''}${e.target}: ${e.message}`;
        return acc;
      }, '');
      dispatch(setError(errorMessage));
    }
    dispatch(setLoadingStatus(false));
  }, [targets, dataSource, dispatch]);

  const runCompare = useCallback(() => {
    if (hasSameTargets) {
      setShowHasSameTargetsModal(true);
    } else {
      handleCompareAudiences();
    }
  }, [hasSameTargets, handleCompareAudiences]);

  const saveDataSource = useCallback(() => {
    updateDataSource(dataSource, dispatch);
  }, [dataSource, dispatch]);

  const runAnalyze = useCallback(() => {
    guardAnalyze(() => {
      saveDataSource();
      onRequestCreateAudience('analyze');
    });
  }, [guardAnalyze, onRequestCreateAudience, saveDataSource]);

  const queryBuilderActions = useMemo(() => {
    const actions = [
      <Box pX="small" pT="small" key="action-analyze">
        <StyledActionItem color="secondary">
          <ListItem
            iconLeft="analize"
            text={translations['en-US'].COMMON.ANALYZE}
            onClick={() => {
              setRunButtonIsOpen(false);
              runAnalyze();
            }}
            data-test-id="analyze-button"
          />
        </StyledActionItem>
      </Box>,
    ];

    if (
      isCompareQuery ||
      targets.length > 1 ||
      !supportsSegmentationAndCompare(dataSource)
    ) {
      return actions;
    }

    if (isAvailableAddon('segmentation')) {
      actions.push([
        <Box borderSize="1px" borderPosition="bottom" key="action-divider" />,
        <Box pX="small" pT="small" key="action-segmentation">
          <StyledActionItem color="secondary">
            <TrialRestricted feature={TRIAL_FEATURES.SEGMENTATION}>
              <ListItem
                iconLeft="segmentation_audience"
                text="Segment"
                onClick={() => {
                  setRunButtonIsOpen(false);
                  saveDataSource();
                  onRequestCreateAudience('segmentation');
                }}
                data-test-id="segmentation-button"
              />
            </TrialRestricted>
          </StyledActionItem>
        </Box>,
      ]);
    }
    return actions;
  }, [
    onRequestCreateAudience,
    targets.length,
    isCompareQuery,
    dataSource,
    runAnalyze,
  ]);

  const checkedTargets = useMemo(() => {
    return (
      targets && targets.every((target) => checkQuery(target.target_query))
    );
  }, [targets]);

  return (
    <React.Fragment>
      <Col span="auto" id="gtAnalyze">
        {queryBuilderActions.length === 1 ? (
          <>
            {isCompareQuery ? (
              <Button
                data-test-id="add-compare-button"
                size="small"
                variant="green"
                disabled={!isQueryValid || isQueryLoading}
                onClick={() => {
                  dispatch(
                    addAudienceToCompare(
                      targets[0],
                      tabs.selectedTabId && tabs.selectedTabId.includes('tmp_')
                        ? tabs.selectedTabId
                        : 'home'
                    )
                  );
                }}
              >
                {translations['en-US'].QUERY_BUILDER.addToCompare}
              </Button>
            ) : (
              <Button
                data-test-id="run-button"
                size="small"
                variant="primary"
                loading={isCheckingTrialStatus || undefined}
                disabled={
                  !checkedTargets || isQueryLoading || isCheckingTrialStatus
                }
                onClick={() => {
                  const requestType =
                    targets && targets.length > 1
                      ? APPS_MAP.COMPARE
                      : 'analyze';
                  if (requestType === 'analyze') {
                    runAnalyze();
                  } else {
                    guardFeature(
                      TRIAL_FEATURES.QUERY_BUILDER_COMPARE,
                      runCompare
                    );
                  }
                }}
              >
                {runButtonLabel}
              </Button>
            )}
          </>
        ) : (
          <Dropdown
            isOpen={runButtonIsOpen}
            position="left"
            fullWidth
            setLargeMinWidth
            content={
              <React.Fragment>
                <Box
                  minHeight={
                    isAvailableAddon('segmentation') ? '110px' : '55px'
                  }
                >
                  <ListGroup items={queryBuilderActions} />
                </Box>
              </React.Fragment>
            }
            onClickOutside={() => {
              setRunButtonIsOpen(false);
            }}
          >
            <Button
              data-test-id="run-button"
              size="small"
              variant="primary"
              loading={isCheckingTrialStatus || undefined}
              disabled={
                !isQueryValid || isQueryLoading || isCheckingTrialStatus
              }
              onClick={() => {
                setRunButtonIsOpen(true);
              }}
            >
              {runButtonLabel}
            </Button>
          </Dropdown>
        )}
      </Col>
      {showHasSameTargetsModal && (
        <Overlay isOpen>
          <Modal
            title="Error"
            footer={
              <ListGroup
                direction="row"
                items={[
                  <Button
                    size="small"
                    variant={'outlined'}
                    onClick={() => setShowHasSameTargetsModal(false)}
                  >
                    Close
                  </Button>,
                ]}
              />
            }
            handleClose={() => setShowHasSameTargetsModal(false)}
          >
            <Box minHeight="100px">
              <span>
                The audiences you tried to compare are too similar. Please try
                editing your queries to make your audiences more distinct.
              </span>
            </Box>
          </Modal>
        </Overlay>
      )}
    </React.Fragment>
  );
};

export default QueryBuilderActions;
